import React, { useEffect, useRef, useState } from 'react';
import ServiceCard from '../../components/common/Cards/ApplicationCategoryCard/ApplicationCategoryCard.component.js';
import './HomePage.styles.scss';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { formatServicesToApplications, getServicesFromStorage } from './serviceFormatter';
import Chip from '@mui/material/Chip';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import FilterApplications from '../../components/FilterApplications/FilterApplications.component.js';
import ChooseUs from '../../components/ChooseUs/ChooseUs.component.js';
import BestPlace from '../../components/BestPlace/BestPlace.component.js';
import CustomerReview from '../../components/CustomerReview/CustomerReview.component.js';
import useTranslation from '../../translations/use-translation.js';

const primaryBtnSx = {
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    padding: '13px 28px',
    borderRadius: '999px',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #1A8089 0%, #0B4A50 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 20px rgba(11, 74, 80, 0.30)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #12636A 0%, #08393D 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 28px rgba(11, 74, 80, 0.40)',
    },
    '&:active': { transform: 'translateY(0)' },
};

const secondaryBtnSx = {
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    padding: '13px 28px',
    borderRadius: '999px',
    textTransform: 'none',
    color: '#12636A',
    borderColor: 'rgba(36, 160, 171, 0.50)',
    '&:hover': {
        background: 'rgba(68, 223, 237, 0.08)',
        borderColor: '#24A0AB',
    },
};

const HomePage = () => {
    const translate = useTranslation();
    const navigate = useNavigate();
    const scrollContainerRefs = useRef([]);
    const applicationCardListContainer = useRef([]);
    const [currentSelectedOptionForService, setCurrentSelectedOptionForService] = useState({ category: 'qualification', value: '' });
    const [currentSelectedOptionServicesData, setCurrentSelectedOptionServicesData] = useState([]);
    const [formattedData, setFormattedData] = useState([]);

    const scrollLeft = (index) => {
        if (scrollContainerRefs.current[index]) {
            scrollContainerRefs.current[index].scrollBy({ left: -600, behavior: 'smooth' });
        }
    };

    const scrollRight = (index) => {
        if (scrollContainerRefs.current[index]) {
            scrollContainerRefs.current[index].scrollBy({ left: 600, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const services = getServicesFromStorage();
        setFormattedData(formatServicesToApplications(services));
    }, []);

    useEffect(() => {
        if (!currentSelectedOptionForService?.category || !formattedData.length) return;
        const group = formattedData.find(
            (item) => item.applicationCategory === currentSelectedOptionForService.category
        );
        if (!group) return;
        let list = group.applicationsList;
        if (currentSelectedOptionForService.value?.length) {
            const match = list.find(
                (item) => item.applicationsCategoryName?.includes(currentSelectedOptionForService.value)
            );
            list = match ? [match] : [];
        }
        setCurrentSelectedOptionServicesData(list);
    }, [currentSelectedOptionForService, formattedData]);

    const headingLines = translate('MAIN_HEADING').split('\n');

    return (
        <main className="homepage-main">

            {/* ── Hero ── */}
            <section className="hero-section">
                <div className="hero-bg-blob hero-bg-blob--1" aria-hidden="true" />
                <div className="hero-bg-blob hero-bg-blob--2" aria-hidden="true" />

                <div className="hero-inner">

                    {/* Left: text content */}
                    <div className="hero-content">
                        <span className="hero-badge">
                            {translate('CONFUSED_HEADING')}
                        </span>

                        <h1 className="hero-heading">
                            <span className="hero-heading-line-1">{headingLines[0]}</span>
                            {headingLines[1] && (
                                <span className="hero-heading-line-2">{headingLines[1]}</span>
                            )}
                        </h1>

                        <p className="hero-subtitle">{translate('SUBTITLE')}</p>

                        <p className="hero-value-prop">{translate('HERO_NO_QUEUE')}</p>

                        <div className="hero-service-chips" aria-label={translate('HERO_SERVICES_TITLE')}>
                            <span className="hero-chip">
                                <ArticleOutlinedIcon fontSize="inherit" />
                                {translate('GOVT_EXAM_FORMS')}
                            </span>
                            <span className="hero-chip">
                                <FingerprintOutlinedIcon fontSize="inherit" />
                                {translate('HERO_AADHAR')}
                            </span>
                            <span className="hero-chip">
                                <CreditCardOutlinedIcon fontSize="inherit" />
                                {translate('HERO_PAN')}
                            </span>
                            <span className="hero-chip">
                                <SchoolOutlinedIcon fontSize="inherit" />
                                {translate('HERO_EXAM')}
                            </span>
                        </div>

                        <div className="hero-cta-row">
                            <Button
                                variant="contained"
                                size="large"
                                disableElevation
                                endIcon={<KeyboardDoubleArrowDownIcon />}
                                onClick={() => navigate('/services')}
                                sx={primaryBtnSx}
                            >
                                {translate('EXPLORE_LATEST')}
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/track-applications')}
                                sx={secondaryBtnSx}
                            >
                                {translate('CHECK_STATUS')}
                            </Button>
                        </div>
                    </div>

                    {/* Right: visual service card */}
                    <div className="hero-visual" aria-hidden="true">
                        <div className="hero-card">
                            <div className="hero-card-header">
                                <span className="hero-card-live-badge">{translate('HERO_LIVE_BADGE')}</span>
                                <span className="hero-card-title">{translate('HERO_SERVICES_TITLE')}</span>
                            </div>

                            <ul className="hero-service-list">
                                <li className="hero-service-item">
                                    <span className="service-icon"><ArticleOutlinedIcon /></span>
                                    <span className="service-name">{translate('GOVT_EXAM_FORMS')}</span>
                                    <span className="service-status service-status--open">{translate('HERO_STATUS_OPEN')}</span>
                                </li>
                                <li className="hero-service-item">
                                    <span className="service-icon"><FingerprintOutlinedIcon /></span>
                                    <span className="service-name">{translate('HERO_AADHAR')}</span>
                                    <span className="service-status service-status--open">{translate('HERO_STATUS_OPEN')}</span>
                                </li>
                                <li className="hero-service-item">
                                    <span className="service-icon"><CreditCardOutlinedIcon /></span>
                                    <span className="service-name">{translate('HERO_PAN')}</span>
                                    <span className="service-status service-status--open">{translate('HERO_STATUS_OPEN')}</span>
                                </li>
                                <li className="hero-service-item">
                                    <span className="service-icon"><LocationOnOutlinedIcon /></span>
                                    <span className="service-name">50+ CSC Centers</span>
                                    <span className="service-status service-status--nearby">{translate('HERO_STATUS_NEARBY')}</span>
                                </li>
                            </ul>

                            <div className="hero-card-footer">
                                <CheckCircleOutlineIcon />
                                <span>{translate('HERO_APPLY_ANYWHERE')}</span>
                            </div>
                        </div>

                        <div className="hero-stat-float hero-stat-float--top">
                            <span className="stat-value">1,000+</span>
                            <span className="stat-label">{translate('HERO_STAT_APPLICATIONS')}</span>
                        </div>
                        <div className="hero-stat-float hero-stat-float--bottom">
                            <span className="stat-value">50+</span>
                            <span className="stat-label">{translate('HERO_STAT_CSC')}</span>
                        </div>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="hero-stats-bar">
                    <div className="hero-stats-inner">
                        <div className="hero-stat-item">
                            <span className="hero-stat-num">1,000+</span>
                            <span className="hero-stat-label">{translate('HERO_STAT_APPLICATIONS')}</span>
                        </div>
                        <div className="hero-stat-divider" aria-hidden="true" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-num">50+</span>
                            <span className="hero-stat-label">{translate('HERO_STAT_CSC')}</span>
                        </div>
                        <div className="hero-stat-divider" aria-hidden="true" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-num">7+</span>
                            <span className="hero-stat-label">{translate('HERO_STAT_STATES')}</span>
                        </div>
                        <div className="hero-stat-divider" aria-hidden="true" />
                        <div className="hero-stat-item">
                            <span className="hero-stat-num">Free</span>
                            <span className="hero-stat-label">{translate('HERO_STAT_FREE')}</span>
                        </div>
                    </div>
                </div>
            </section>

            <FilterApplications
                applicationCardListContainerRef={applicationCardListContainer}
                currentSelectedOptionForService={currentSelectedOptionForService}
                setCurrentSelectedOptionForService={setCurrentSelectedOptionForService}
            />

            <BestPlace />

            <section className="oa-service-category-section-header" ref={applicationCardListContainer}>
                <h2>{translate('WHAT_APPLY_TODAY')}</h2>
            </section>

            <section className="applications-showcase">
                {currentSelectedOptionForService.value?.length && currentSelectedOptionServicesData?.length === 0 ? (
                    <div className="applications-empty">
                        <span className="applications-empty__icon">
                            <SearchOffOutlinedIcon />
                        </span>
                        <h3 className="applications-empty__title">
                            No applications found for “{currentSelectedOptionForService.value}”
                        </h3>
                        <p className="applications-empty__text">
                            We don’t have any services listed for this state / UT yet.
                            Try another state or browse all available services.
                        </p>
                        <Button
                            variant="contained"
                            disableElevation
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/services')}
                            sx={primaryBtnSx}
                        >
                            {translate('EXPLORE_LATEST')}
                        </Button>
                    </div>
                ) : currentSelectedOptionServicesData?.map((item, index) => (
                    <article key={`application-category-${index}`} className="general-application-card-list-container">
                        <header className="application-qualification-header">
                            <Chip
                                label={item?.applicationsCategoryName}
                                variant="filled"
                                color="primary"
                                size="medium"
                            />
                        </header>

                        <button
                            className="scroll-button scroll-button-left"
                            onClick={() => scrollLeft(index)}
                            aria-label="Scroll left"
                            type="button"
                        >
                            <ArrowBackIosIcon />
                        </button>

                        <div
                            className="general-application-card-list"
                            ref={(el) => (scrollContainerRefs.current[index] = el)}
                            role="region"
                            aria-label={`${item?.applicationsCategoryName} applications`}
                        >
                            {item?.applicationsListByCategory?.map((app, appIndex) => (
                                <ServiceCard
                                    key={`application-${index}-${appIndex}`}
                                    applicationName={app?.applicationName}
                                    applicationStartDate={app?.applicationStartDate}
                                    applicationImageURL={app?.applicationImageURL}
                                    applicationDescription={app?.applicationDescription}
                                    applicationLastDate={app?.applicationLastDate}
                                    applicationsCategoryName={item?.applicationsCategoryName}
                                    serviceState={app?.serviceState}
                                />
                            ))}
                        </div>

                        <button
                            className="scroll-button scroll-button-right"
                            onClick={() => scrollRight(index)}
                            aria-label="Scroll right"
                            type="button"
                        >
                            <ArrowForwardIosIcon />
                        </button>
                    </article>
                ))}
            </section>

            <CustomerReview />
            <ChooseUs />
        </main>
    );
};

export default HomePage;
